import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import SeoHead from "../components/SeoHead";
import { api, resolveUrl, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useSettings } from "../contexts/SettingsContext";
import { fmtDate } from "../lib/dateFormat";
import { CreditCard, CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";

export function PayStart() {
  const { id } = useParams();
  const { lang } = useI18n();
  const { settings } = useSettings();
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/reservations/${id}/public-summary`)
      .then((r) => setRes(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [id]);

  const depositPct = settings?.payment?.deposit_pct ?? 30;
  const paymentEnabled = settings?.payment?.enabled;
  const depositAmount = res ? Math.round((res.total_eur * depositPct) / 100 * 100) / 100 : 0;

  const startCheckout = async () => {
    setLoading(true); setError("");
    try {
      const origin = window.location.origin;
      const r = await api.post("/payments/checkout", {
        reservation_id: id,
        origin_url: origin,
        lang,
      });
      window.location.href = r.data.url;
    } catch (e) {
      setError(formatApiError(e));
      setLoading(false);
    }
  };

  const t = (ro, en) => (lang === "en" ? en : ro);
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--brand-bg)" }}>
      <SeoHead title={t("Plata avans rezervare", "Reservation deposit payment")} description={t("Plateste online avansul pentru rezervarea ta.", "Pay your reservation deposit online securely.")} />
      <PublicHeader />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 md:px-12 py-16">
        <div className="label-eyebrow mb-3" style={{ color: "var(--brand-primary)" }}>{t("PLATA SECURIZATA", "SECURE PAYMENT")}</div>
        <h1 className="font-serif text-4xl mb-8">{t("Plateste avansul", "Pay deposit")}</h1>

        {error && <div className="card-surface p-4 mb-6 text-sm" style={{ color: "#ef4444" }} data-testid="pay-error">{error}</div>}

        {!res ? (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--brand-text-secondary)" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> {t("Se incarca...", "Loading...")}
          </div>
        ) : (
          <div className="card-surface p-8 space-y-6" data-testid="pay-card">
            <div className="flex gap-5">
              {res.car_image?.url && (
                <img src={resolveUrl(res.car_image.url)} alt={res.car_name} className="w-32 h-24 object-cover rounded" />
              )}
              <div className="flex-1">
                <div className="font-serif text-2xl mb-1">{res.car_name}</div>
                <div className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>
                  {fmtDate(res.start_date)} → {fmtDate(res.end_date)} &middot; {res.days} {t("zile", "days")}
                </div>
                <div className="text-sm mt-1" style={{ color: "var(--brand-text-secondary)" }}>{res.guest_name}</div>
              </div>
            </div>

            <div className="border-t pt-5" style={{ borderColor: "var(--brand-border)" }}>
              <div className="flex justify-between text-sm mb-2">
                <span style={{ color: "var(--brand-text-secondary)" }}>{t("Total rezervare", "Reservation total")}</span>
                <span>€{res.total_eur.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span style={{ color: "var(--brand-text-secondary)" }}>{t("Avans", "Deposit")} ({depositPct}%)</span>
                <span style={{ color: "var(--brand-primary)" }}>€{depositAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs pt-2" style={{ color: "var(--brand-text-secondary)" }}>
                <span>{t("Restul se plateste catre proprietar la preluare.", "The remainder is paid offline at pickup.")}</span>
              </div>
            </div>

            {res.payment_status === "paid" ? (
              <div className="flex items-center gap-2 text-sm" style={{ color: "#22c55e" }} data-testid="already-paid">
                <CheckCircle2 className="w-5 h-5" /> {t("Plata deja efectuata", "Already paid")}
              </div>
            ) : !paymentEnabled ? (
              <div className="text-sm" style={{ color: "var(--brand-text-secondary)" }} data-testid="payment-disabled">
                {t("Plata online este momentan dezactivata. Plata se va face direct la proprietar.", "Online payment is currently disabled. Pay the owner directly at pickup.")}
              </div>
            ) : (
              <button onClick={startCheckout} disabled={loading} className="btn-gold-filled w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="start-checkout-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                {t("Plateste cu cardul", "Pay with card")} — €{depositAmount.toFixed(2)}
              </button>
            )}

            <div className="flex items-center gap-2 text-xs justify-center pt-2" style={{ color: "var(--brand-text-secondary)" }}>
              <ShieldCheck className="w-4 h-4" /> {t("Procesare securizata prin Stripe", "Secure processing via Stripe")}
            </div>
          </div>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}

export function PaySuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const rid = params.get("rid");
  const { lang } = useI18n();
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [error, setError] = useState("");

  const t = (ro, en) => (lang === "en" ? en : ro);

  const poll = useCallback(async (attempt) => {
    if (attempt > 10) {
      setError(t("Verificarea statusului a expirat. Verifica emailul pentru confirmare.", "Status check timed out. Please check your email."));
      return;
    }
    try {
      const r = await api.get(`/payments/status/${sessionId}`);
      setStatus(r.data);
      if (r.data.payment_status === "paid") return;
      if (r.data.status === "expired") {
        setError(t("Sesiunea de plata a expirat.", "Payment session expired."));
        return;
      }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [sessionId, lang]);

  useEffect(() => {
    if (sessionId) poll(0);
  }, [sessionId, poll]);

  const paid = status.payment_status === "paid";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--brand-bg)" }}>
      <SeoHead title={paid ? t("Plata confirmata", "Payment confirmed") : t("Procesare plata", "Processing payment")} />
      <PublicHeader />
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="card-surface p-12 max-w-lg w-full text-center" data-testid="pay-success-card">
          {paid ? (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto mb-6" style={{ color: "#22c55e" }} />
              <h1 className="font-serif text-3xl mb-3" style={{ color: "var(--brand-primary)" }}>{t("Multumim!", "Thank you!")}</h1>
              <p className="text-sm mb-2" style={{ color: "var(--brand-text-secondary)" }}>
                {t("Plata a fost procesata cu succes. Proprietarul va fi notificat.", "Your payment has been processed. The owner has been notified.")}
              </p>
              {status.amount_total ? (
                <p className="text-sm" style={{ color: "var(--brand-primary)" }}>
                  €{(status.amount_total / 100).toFixed(2)}
                </p>
              ) : null}
            </>
          ) : error ? (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-6" style={{ color: "#ef4444" }} />
              <h1 className="font-serif text-3xl mb-3">{t("Eroare", "Error")}</h1>
              <p className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>{error}</p>
            </>
          ) : (
            <>
              <Loader2 className="w-12 h-12 mx-auto mb-6 animate-spin" style={{ color: "var(--brand-primary)" }} />
              <h1 className="font-serif text-2xl mb-3">{t("Verificam plata...", "Verifying payment...")}</h1>
              <p className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>{t("Va rugam asteptati.", "Please wait.")}</p>
            </>
          )}
          <div className="mt-8">
            <Link to="/" className="btn-ghost inline-block" data-testid="back-home-btn">{t("Inapoi la pagina principala", "Back to homepage")}</Link>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

export function PayCancel() {
  const [params] = useSearchParams();
  const rid = params.get("rid");
  const { lang } = useI18n();
  const t = (ro, en) => (lang === "en" ? en : ro);
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--brand-bg)" }}>
      <SeoHead title={t("Plata anulata", "Payment cancelled")} />
      <PublicHeader />
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="card-surface p-12 max-w-lg w-full text-center" data-testid="pay-cancel-card">
          <XCircle className="w-16 h-16 mx-auto mb-6" style={{ color: "#ef4444" }} />
          <h1 className="font-serif text-3xl mb-3">{t("Plata anulata", "Payment cancelled")}</h1>
          <p className="text-sm mb-6" style={{ color: "var(--brand-text-secondary)" }}>
            {t("Nu am procesat nicio plata. Poti reincerca oricand.", "No payment was processed. You can try again anytime.")}
          </p>
          {rid && (
            <Link to={`/pay/${rid}`} className="btn-gold-filled inline-block" data-testid="retry-pay-btn">{t("Incearca din nou", "Try again")}</Link>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

export default PayStart;
